import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Row, Table } from "react-bootstrap";
import { Field, Form as FormikForm, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import "./SessionHistory.css";
import { getAllShootingEventsConfigs } from "../../configs/utils/ShootingEventConfigFactory";
import { getRememberedShootingEventId, rememberShootingEventId } from "../../configs/utils/ShootingEventSelectionStorage";
import { useAuth } from "../../contexts/AuthContext";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { ShootingEventConfig } from "../../types/ShootingEventConfig";
import { ShootingSession } from "../../types/ShootingSession";
import { backendURL, useApiFetch } from "../../utils/api";
import FormInput from "../../utils/form/FormInput";
import {
  filterSessions,
  getSessionTargetCount,
  getSessionTotalScore,
  getTotalShotsCount,
  SessionHistoryFilters,
  SessionHistorySortOrder
} from "./sessionHistoryUtils";

const createInitialFilters = (shootingEventId: string): SessionHistoryFilters => ({
  shootingEventId,
  dateFrom: "",
  dateTo: "",
  shotCount: "",
  sortOrder: "dateDesc"
});

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString("pl-PL");
}

function SessionHistory() {
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { getAccountId } = useAuth();
  const { confirm } = useConfirmation();
  const { success } = useNotifications();
  const shootingEventConfigs = useMemo<ShootingEventConfig[]>(() => getAllShootingEventsConfigs(), []);
  const defaultShootingEventId = useMemo(
    () => getRememberedShootingEventId(shootingEventConfigs),
    [shootingEventConfigs]
  );

  const [sessions, setSessions] = useState<ShootingSession[]>([]);
  const [filters, setFilters] = useState<SessionHistoryFilters>(() => createInitialFilters(defaultShootingEventId));
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const filteredSessions = useMemo(() => filterSessions(sessions, filters), [sessions, filters]);
  const filtersApplied = Boolean(
    filters.dateFrom ||
    filters.dateTo ||
    filters.shotCount ||
    filters.shootingEventId !== defaultShootingEventId ||
    filters.sortOrder !== "dateDesc"
  );

  useEffect(() => {
    const accountId = getAccountId();
    if (!accountId) {
      setHasLoaded(true);
      return;
    }

    const fetchSessions = async () => {
      try {
        const data = await apiFetch<ShootingSession[]>(`${backendURL}/shooting-sessions/reporter/${accountId}`, {
          method: "GET",
          includeAuth: true
        });

        setSessions(data ?? []);
      } catch (err) {
        console.error("Error fetching user sessions:", err);
      } finally {
        setHasLoaded(true);
      }
    };

    fetchSessions();
  }, []);

  const handleFilterChange = <K extends keyof SessionHistoryFilters>(name: K, value: SessionHistoryFilters[K]) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters(createInitialFilters(defaultShootingEventId));
  };

  const handleDeleteSession = async (session: ShootingSession) => {
    const confirmed = await confirm({ message: "Czy na pewno chcesz usunąć sesję?" });
    if (!confirmed) {
      return;
    }

    try {
      await apiFetch(`${backendURL}/shooting-sessions`, {
        method: "DELETE",
        includeAuth: true,
        body: JSON.stringify({
          shootingSessionId: session.id,
        })
      });

      setSessions((prev) => prev.filter((currentSession) => currentSession.id !== session.id));
      success("Sesja została usunięta");
    } catch (err) {
      console.error("Error deleting session:", err);
    }
  };

  const renderEmptyState = () => {
    if (!hasLoaded) {
      return null;
    }

    if (sessions.length === 0) {
      return (
        <div className="session-history-empty">
          <p className="mb-3">Nie masz jeszcze zapisanych sesji.</p>
          <Button variant="dark" onClick={() => navigate("/new-session")}>Dodaj pierwszą sesję</Button>
        </div>
      );
    }

    if (filteredSessions.length === 0) {
      return (
        <div className="session-history-empty">
          <p className="mb-3">Brak sesji spełniających wybrane filtry.</p>
          <Button variant="secondary" onClick={resetFilters}>Wyczyść filtry</Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Container>
      <Row>
        <Col xl={10} lg={11} xs={12} className="mx-auto">
          <div className="py-3 session-history-page">
            <h1 className="font-weight-light text-center mb-4 session-history-title">Moje sesje</h1>

            {!getAccountId() && (
              <Alert variant="warning">Nie udało się odczytać identyfikatora konta. Zaloguj się ponownie.</Alert>
            )}

            <Formik<SessionHistoryFilters>
              initialValues={filters}
              enableReinitialize
              onSubmit={() => undefined}
            >
              {({ setFieldValue }) => (
                <FormikForm className="mb-3 session-history-filters">
                  <div className="session-history-event-filter">
                    <label htmlFor="sessionHistoryShootingEvent" className="form-label">Konkurencja</label>
                    <Field
                      as="select"
                      id="sessionHistoryShootingEvent"
                      name="shootingEventId"
                      className="form-select"
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                        const value = event.target.value;
                        rememberShootingEventId(value);
                        setFieldValue("shootingEventId", value);
                        handleFilterChange("shootingEventId", value);
                      }}
                    >
                      {shootingEventConfigs.map((config) => (
                        <option key={config.shootingEventId} value={config.shootingEventId}>
                          {config.shootingEventName}
                        </option>
                      ))}
                    </Field>
                  </div>

                  <div className="session-history-date-filter">
                    <label htmlFor="sessionHistoryDateFrom" className="form-label">Od</label>
                    <FormInput
                      name="dateFrom"
                      placeholder="dd.mm.rrrr"
                      type="date"
                      className=""
                      inputId="sessionHistoryDateFrom"
                      required={false}
                      onValueChange={(value) => handleFilterChange("dateFrom", value)}
                    />
                  </div>

                  <div className="session-history-date-filter">
                    <label htmlFor="sessionHistoryDateTo" className="form-label">Do</label>
                    <FormInput
                      name="dateTo"
                      placeholder="dd.mm.rrrr"
                      type="date"
                      className=""
                      inputId="sessionHistoryDateTo"
                      required={false}
                      onValueChange={(value) => handleFilterChange("dateTo", value)}
                    />
                  </div>

                  <div className="session-history-shot-filter">
                    <label htmlFor="sessionHistoryShotCount" className="form-label">Liczba strzałów</label>
                    <Field
                      id="sessionHistoryShotCount"
                      name="shotCount"
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="form-control"
                      placeholder="np. 10"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = event.target.value;
                        setFieldValue("shotCount", value);
                        handleFilterChange("shotCount", value);
                      }}
                    />
                  </div>

                  <div className="session-history-sort-filter">
                    <label htmlFor="sessionHistorySortBy" className="form-label">Sortuj wg</label>
                    <Field
                      as="select"
                      id="sessionHistorySortBy"
                      name="sortOrder"
                      className="form-select"
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                        const value = event.target.value as SessionHistorySortOrder;
                        setFieldValue("sortOrder", value);
                        handleFilterChange("sortOrder", value);
                      }}
                    >
                      <option value="dateDesc">Najnowsze</option>
                      <option value="dateAsc">Najstarsze</option>
                      <option value="totalScoreDesc">Najwięcej punktów</option>
                      <option value="totalScoreAsc">Najmniej punktów</option>
                    </Field>
                  </div>

                  <Button
                    variant="secondary"
                    className="session-history-action"
                    disabled={!filtersApplied}
                    type="button"
                    onClick={resetFilters}
                  >
                    Wyczyść filtry
                  </Button>
                </FormikForm>
              )}
            </Formik>

            {renderEmptyState()}

            {filteredSessions.length > 0 && (
              <Table bordered hover responsive className="text-center align-middle session-history-table">
                <thead className="table-dark">
                  <tr>
                    <th>Data</th>
                    <th>Suma punktów</th>
                    <th>Liczba strzałów</th>
                    <th>Tarcze</th>
                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSessions.map((session) => (
                    <tr
                      key={session.id}
                      className="session-history-row"
                      onClick={() => navigate(`/session-reports/${session.id}`)}
                    >
                      <td>{formatDate(session.sessionDate)}</td>
                      <td>{getSessionTotalScore(session)}</td>
                      <td>{getTotalShotsCount(session)}</td>
                      <td>{getSessionTargetCount(session)}</td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteSession(session);
                          }}
                        >
                          Usuń
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default SessionHistory;
